import { Button } from '../primitives';
import { useCookieConsent } from './useCookieConsent';
import { useHydrated } from '../lib/useHydrated';

export interface CookieBannerProps {
  /** Path to the cookie policy page (defaults to `/cookies`). */
  policyHref?: string;
}

/**
 * Bottom-of-viewport cookie consent banner. Renders only while the user has
 * not made a choice (status === 'pending'). Two-button MVP: Essential only /
 * Accept all. Add a "Customise" path later if granular control is needed.
 *
 * Mount once in the marketing root layout. Banner is intentionally not modal
 * — it does not trap focus or block interaction; users can browse and decide
 * when ready.
 */
export function CookieBanner({ policyHref = '/cookies' }: CookieBannerProps) {
  const { consent, set } = useCookieConsent();
  const hydrated = useHydrated();

  // Hidden until hydrated (Slice 1C, AR-P7). The consent hook's server
  // snapshot is "pending", so without this the banner was baked into every
  // prerendered page and flashed for visitors who had already chosen. The
  // portal is client-rendered, where `hydrated` is true on the first render.
  if (!hydrated || consent.status !== 'pending') return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-neutral-900/95 backdrop-blur-md shadow-2xl shadow-black/40"
    >
      <div className="container mx-auto flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:gap-6 md:py-5">
        <div className="flex-1 text-sm text-gray-300">
          <p className="mb-1 font-semibold text-white">We use cookies</p>
          <p className="text-gray-400">
            Essential cookies keep this site working. Optional analytics help us understand
            how you use it. Read our{' '}
            <a href={policyHref} className="text-primary underline-offset-2 hover:underline">
              Cookie Policy
            </a>{' '}
            for details. You can change your choice anytime.
          </p>
        </div>
        <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row md:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => set({ status: 'essential' })}
            className="border-white/20 bg-transparent text-gray-300 hover:bg-white/10 hover:text-white"
          >
            Essential only
          </Button>
          <Button size="sm" onClick={() => set({ status: 'all' })}>
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
